import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import { useAppContext } from '../../libs/contextLib';
import { filterItems } from './BookingFilters';
import useAllCharters from '../Charters/useAllCharters';
import { charterModelData } from '../../services/ModelService';
import { Charter } from '../../model/charter';

const BookingContext = createContext();

export function BookingProvider({ children }) {
    const { messageApi } = useAppContext();
    const { allCharters, allChartersArray } = useAllCharters();
    const [filterBy, setFilterBy] = useState([]);
    const [filter, setFilter] = useState('');
    const [updated, setUpdated] = useState(false);
    const [saving, setSaving] = useState(false);

    // Callbacks for events
    const [callbacks, setCallbacks] = useState({});

    // Register a callback for a specific event
    const registerCallback = useCallback((event, callback) => {
        setCallbacks((prevCallbacks) => ({
            ...prevCallbacks,
            [event]: [...(prevCallbacks[event] || []), callback],
        }));
    }, []);

    // Unregister a callback for a specific event
    const unregisterCallback = useCallback((event, callback) => {
        setCallbacks((prevCallbacks) => ({
            ...prevCallbacks,
            [event]: (prevCallbacks[event] || []).filter((cb) => cb !== callback),
        }));
    }, []);

    // Trigger callbacks for a specific event
    const triggerCallbacks = useCallback(
        (event, ...args) => {
            if (callbacks[event]) {
                callbacks[event].forEach((callback) => callback(...args));
            }
        },
        [callbacks]
    );

    // Memoized values
    const filteredBookings = useMemo(() => {
        if (!allChartersArray?.length) return [];
        let filtered = [...allChartersArray];

        // Apply status filters
        if (filterBy.length) {
            filtered = filtered.filter((booking) =>
                filterBy.some((filter) => filterItems[filter].showFn(booking))
            );
        }

        // Apply text search
        if (filter) {
            const searchTerm = filter.toLowerCase();
            filtered = filtered.filter(
                (booking) =>
                    booking.description?.toLowerCase().includes(searchTerm) ||
                    booking.bookingReference?.toLowerCase().includes(searchTerm) || 
                    booking.name?.toLowerCase().includes(searchTerm) ||
                    booking.customer?.name?.toLowerCase().includes(searchTerm)
            );
        }

        return filtered;
    }, [allChartersArray, filterBy, filter]);

    const createBooking = async (booking) => {
        setSaving(true);
        try {
            const charter = new Charter(booking);
            await charterModelData.save(charter);
            setUpdated(true);
            triggerCallbacks('bookingCreated', charter);
        } catch (err) {
            console.error('Failed to create booking:', err);
        } finally {
            setSaving(false);
        }
    };

    const updateBooking = async (booking) => {
        setSaving(true);
        try {
            const charter = new Charter(booking);
            await charterModelData.save(charter);
            setUpdated(true);
            triggerCallbacks('bookingUpdated', charter);
        } catch (err) {
            console.error('Failed to update booking:', err);
        } finally {
            setSaving(false);
        }
    };

    const deleteBooking = async (bookingId) => {
        setSaving(true);
        try {
            await charterModelData.delete(bookingId);
            setUpdated(true);
            triggerCallbacks('bookingDeleted', bookingId);
        } catch (err) {
            console.error('Failed to delete booking:', err);
        } finally {
            setSaving(false);
        }
    };

    const value = {
        bookings: allChartersArray,
        charters: allChartersArray,
        filteredBookings,
        isLoading: !allChartersArray,
        filterBy,
        setFilterBy,
        filter,
        setFilter,
        updated,
        setUpdated,
        saving,
        createBooking,
        updateBooking,
        deleteBooking,
        registerCallback,
        unregisterCallback,
        triggerCallbacks
    };

    return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
}

export function useBooking() {
    const context = useContext(BookingContext);
    if (!context) {
        throw new Error('useBooking must be used within a BookingProvider');
    }
    return context;
} 